/**
 * 대시보드 — 일일 변동 현황 (전일 대비) 섹션. admin/mgmt 전용 (2026-07-10)
 *
 * 영업지원 담당자의 일일 데이터 검증용:
 *   날짜 선택 → [조회] → 그날 하루 동안의
 *   · 영업진행 발주/납품/발행/매출 금액 합계 변동 + 변동 건별 상세
 *   · 유지보수 매출 합계 변동 + 변동 건별 상세
 *   · 재고 전체 수량 합계 변동 + 변동 품목별 상세
 *   변동 없는 항목은 생략. [엑셀]로 전체 내려받기(요약 시트는 변동 0 포함).
 *
 * 데이터: GET /dashboard/daily-changes?date=... (매일 자정 스냅샷 비교 — 기능
 * 도입일 이후부터 조회 가능. 오늘 날짜는 '지금까지의 변동'을 실시간 비교)
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateInput } from './DateInput';
import * as dashboardApi from '../lib/dashboard-api';
import type {
  DailyChangeBucket,
  DailyChangeFieldChange,
} from '../lib/dashboard-api';

function todayISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fmtNum(n: number): string {
  return n.toLocaleString('ko-KR');
}

function fmtDelta(n: number): string {
  return `${n > 0 ? '+' : ''}${n.toLocaleString('ko-KR')}`;
}

function deltaCls(n: number): string {
  if (n > 0) return 'text-blue-600';
  if (n < 0) return 'text-red-600';
  return 'text-slate-500';
}

/** '2026-07-10 00:05:12.123+09' → '2026-07-10 00:05' */
function fmtTs(ts?: string): string {
  if (!ts) return '';
  return ts.slice(0, 16);
}

const CHANGE_TYPE_CLS: Record<string, string> = {
  신규: 'bg-blue-100 text-blue-700',
  수정: 'bg-amber-100 text-amber-700',
  삭제: 'bg-red-100 text-red-700',
};

function ChangeTypeBadge({ type }: { type: string }) {
  return (
    <span className={`text-[11px] px-1.5 py-0.5 rounded font-semibold whitespace-nowrap ${CHANGE_TYPE_CLS[type] ?? 'bg-slate-100 text-slate-600'}`}>
      {type}
    </span>
  );
}

function ChangeList({ changes }: { changes: DailyChangeFieldChange[] }) {
  return (
    <div className="space-y-0.5">
      {changes.map((c, i) => (
        <div key={i} className="text-xs">
          <span className="text-slate-500">{c.label}:</span>{' '}
          <span className="text-slate-400 line-through">{c.old}</span>
          <span className="mx-1 text-slate-400">→</span>
          <span className="font-semibold text-slate-800">{c.new}</span>
        </div>
      ))}
    </div>
  );
}

/** 변동된 버킷만 표시 (변동 없는 항목 생략 — 요청사항) */
function BucketRows({ group, buckets }: { group: string; buckets: DailyChangeBucket[] }) {
  const changed = buckets.filter((b) => b.delta !== 0);
  if (changed.length === 0) return null;
  return (
    <>
      {changed.map((b) => (
        <tr key={`${group}-${b.name}`} className="border-b border-slate-100">
          <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{group}</td>
          <td className="px-3 py-2 font-semibold whitespace-nowrap">{b.name}</td>
          <td className="px-3 py-2 text-right num text-slate-500">{fmtNum(b.before)}</td>
          <td className="px-3 py-2 text-right num">{fmtNum(b.after)}</td>
          <td className={`px-3 py-2 text-right num font-bold ${deltaCls(b.delta)}`}>{fmtDelta(b.delta)}</td>
        </tr>
      ))}
    </>
  );
}

export function DailyChangesSection() {
  const [date, setDate] = useState<string>(todayISO());
  const [queriedDate, setQueriedDate] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['dashboard', 'daily-changes', queriedDate],
    queryFn: () => dashboardApi.getDailyChanges(queriedDate!),
    enabled: !!queriedDate,
    staleTime: 30_000,
  });
  const data = q.data;

  function handleQuery() {
    setDateError(null);
    if (!date) return;
    if (date > todayISO()) {
      setDateError('오늘 이후 날짜는 조회할 수 없습니다.');
      return;
    }
    if (queriedDate === date) {
      // 같은 날짜 재조회 (오늘 = 실시간 비교 갱신) — queryKey 불변이라 명시 refetch
      void q.refetch();
    } else {
      setQueriedDate(date);
    }
  }

  const changedBucketCount =
    (data?.sales?.buckets.filter((b) => b.delta !== 0).length ?? 0) +
    (data?.maint?.buckets.filter((b) => b.delta !== 0).length ?? 0) +
    (data?.stock && data.stock.delta !== 0 ? 1 : 0);
  const detailCount =
    (data?.sales?.details.length ?? 0) +
    (data?.maint?.details.length ?? 0) +
    (data?.stock?.details.length ?? 0);
  const noChanges = data?.ready === true && changedBucketCount === 0 && detailCount === 0;

  async function handleDownload() {
    if (!queriedDate) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await dashboardApi.downloadDailyChangesExcel(queriedDate);
    } catch (e) {
      setDownloadError((e as Error).message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 p-4 md:p-5 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div className="text-sm font-semibold">
          📋 일일 변동 현황 <span className="text-slate-400 font-normal">(전일 대비 · 관리자 전용)</span>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        선택한 날짜 하루 동안 바뀐 영업진행 금액·유지보수 금액·재고 수량을 전날 기준과 비교해 보여줍니다.
        변동 없는 항목은 표시하지 않습니다. 오늘 날짜는 &lsquo;지금까지의 변동&rsquo;입니다.
      </p>

      {/* 조회 컨트롤 */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <DateInput value={date} onChange={setDate} className="w-44" max={todayISO()} />
        <button
          type="button"
          onClick={handleQuery}
          disabled={!date || q.isFetching}
          className="btn btn-primary"
        >
          {q.isFetching ? '조회 중…' : '조회'}
        </button>
        <button
          type="button"
          onClick={() => void handleDownload()}
          disabled={!data?.ready || downloading}
          className="btn btn-success"
          title="조회한 날짜의 변동 내역 엑셀 다운로드"
        >
          {downloading ? '다운로드 중…' : '📥 엑셀'}
        </button>
      </div>
      {dateError && <div className="text-xs text-red-600 mb-2">{dateError}</div>}
      {downloadError && (
        <div className="text-xs text-red-600 mb-2">엑셀 다운로드 실패: {downloadError}</div>
      )}

      {q.isError && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
          조회 실패: {(q.error as Error).message}
        </div>
      )}

      {data && !data.ready && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
          {data.reason}
        </div>
      )}

      {data?.ready && (
        <>
          {/* 비교 구간 안내 */}
          <div className="text-xs text-slate-500 mb-3">
            비교 구간: {fmtTs(data.periodFrom)} 기준 → {fmtTs(data.periodTo)}
            {data.periodToLive ? ' (현재 시점)' : ''} 기준
            {(data.spanDays ?? 1) > 1 && (
              <span className="text-amber-600 font-semibold">
                {' '}
                · ⚠ 중간 날짜 기준 데이터가 없어 {data.spanDays}일치가 합산되어 보입니다
              </span>
            )}
          </div>

          {noChanges && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-4 text-sm text-emerald-700 text-center">
              ✅ 이 날은 변동이 없습니다.
            </div>
          )}

          {/* 합계 변동 (변동 있는 항목만) */}
          {changedBucketCount > 0 && data.sales && data.maint && data.stock && (
            <div className="mb-4">
              <div className="text-xs text-slate-500 mb-1">
                💡 합계(영업진행·유지보수)는{' '}
                <strong className="text-slate-700">{data.bucketYear ?? data.date.slice(0, 4)}년</strong>{' '}
                기준입니다 (재고는 전체 · 아래 변동 상세는 연도 무관 전체 표시).
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-[13px] border-collapse">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">구분</th>
                    <th className="text-left px-3 py-2 font-semibold">항목</th>
                    <th className="text-right px-3 py-2 font-semibold">기준 (그날 시작)</th>
                    <th className="text-right px-3 py-2 font-semibold">비교 시점</th>
                    <th className="text-right px-3 py-2 font-semibold">변동</th>
                  </tr>
                </thead>
                <tbody>
                  <BucketRows group="영업진행" buckets={data.sales.buckets} />
                  <BucketRows group="유지보수" buckets={data.maint.buckets} />
                  {data.stock.delta !== 0 && (
                    <tr className="border-b border-slate-100">
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">재고</td>
                      <td className="px-3 py-2 font-semibold whitespace-nowrap">전체 수량 합계</td>
                      <td className="px-3 py-2 text-right num text-slate-500">{fmtNum(data.stock.totalBefore)}</td>
                      <td className="px-3 py-2 text-right num">{fmtNum(data.stock.totalAfter)}</td>
                      <td className={`px-3 py-2 text-right num font-bold ${deltaCls(data.stock.delta)}`}>
                        {fmtDelta(data.stock.delta)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* 영업진행 변동 상세 */}
          {(data.sales?.details.length ?? 0) > 0 && (
            <DetailBlock title={`영업진행 변동 상세 (${data.sales!.details.length}건)`}>
              <table className="w-full text-[13px] border-collapse">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-semibold">매출번호</th>
                    <th className="text-left px-3 py-1.5 font-semibold">엔드유저 / 프로젝트</th>
                    <th className="text-center px-3 py-1.5 font-semibold">구분</th>
                    <th className="text-left px-3 py-1.5 font-semibold">변동 내용</th>
                    <th className="text-right px-3 py-1.5 font-semibold">금액 영향</th>
                    <th className="text-left px-3 py-1.5 font-semibold">변경자</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sales!.details.map((d) => (
                    <tr key={d.salesId} className="border-b border-slate-100 align-top">
                      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{d.salesId}</td>
                      <td className="px-3 py-2">
                        <div>{d.endUser ?? '-'}</div>
                        {d.projectName && (
                          <div className="text-xs text-slate-500 truncate max-w-[240px]">{d.projectName}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center"><ChangeTypeBadge type={d.changeType} /></td>
                      <td className="px-3 py-2"><ChangeList changes={d.changes} /></td>
                      <td className={`px-3 py-2 text-right num whitespace-nowrap ${deltaCls(d.amountDelta)}`}>
                        {d.amountDelta !== 0 ? fmtDelta(d.amountDelta) : '-'}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">{d.users.join(', ') || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DetailBlock>
          )}

          {/* 유지보수 변동 상세 */}
          {(data.maint?.details.length ?? 0) > 0 && (
            <DetailBlock title={`유지보수 변동 상세 (${data.maint!.details.length}건)`}>
              <table className="w-full text-[13px] border-collapse">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-semibold">계약명</th>
                    <th className="text-left px-3 py-1.5 font-semibold">엔드유저</th>
                    <th className="text-left px-3 py-1.5 font-semibold">귀속 월</th>
                    <th className="text-center px-3 py-1.5 font-semibold">구분</th>
                    <th className="text-left px-3 py-1.5 font-semibold">변동 내용</th>
                    <th className="text-right px-3 py-1.5 font-semibold">금액 영향</th>
                    <th className="text-left px-3 py-1.5 font-semibold">변경자</th>
                  </tr>
                </thead>
                <tbody>
                  {data.maint!.details.map((d) => (
                    <tr key={d.maintenanceId} className="border-b border-slate-100 align-top">
                      <td className="px-3 py-2 max-w-[240px]">
                        <div className="truncate">{d.name ?? '-'}</div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{d.endUser ?? '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs">{d.ym}</td>
                      <td className="px-3 py-2 text-center"><ChangeTypeBadge type={d.changeType} /></td>
                      <td className="px-3 py-2"><ChangeList changes={d.changes} /></td>
                      <td className={`px-3 py-2 text-right num whitespace-nowrap ${deltaCls(d.amountDelta)}`}>
                        {d.amountDelta !== 0 ? fmtDelta(d.amountDelta) : '-'}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">{d.users.join(', ') || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DetailBlock>
          )}

          {/* 재고 변동 상세 */}
          {(data.stock?.details.length ?? 0) > 0 && (
            <DetailBlock title={`재고 수량 변동 상세 (${data.stock!.details.length}품목)`}>
              <table className="w-full text-[13px] border-collapse">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-semibold">품목</th>
                    <th className="text-right px-3 py-1.5 font-semibold">기준 수량</th>
                    <th className="text-right px-3 py-1.5 font-semibold">비교 시점</th>
                    <th className="text-right px-3 py-1.5 font-semibold">변동</th>
                    <th className="text-left px-3 py-1.5 font-semibold">변경자</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stock!.details.map((d) => (
                    <tr key={d.itemCode} className="border-b border-slate-100">
                      <td className="px-3 py-2">
                        <strong>{d.itemName ?? d.itemCode}</strong>{' '}
                        <span className="text-slate-500 text-xs font-mono">{d.itemCode}</span>
                        {d.isKeyStock && (
                          <span className="ml-1 text-[11px] px-1 py-0.5 rounded bg-slate-200 text-slate-600">주요</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right num text-slate-500">{fmtNum(d.before)}</td>
                      <td className="px-3 py-2 text-right num">{fmtNum(d.after)}</td>
                      <td className={`px-3 py-2 text-right num font-bold ${deltaCls(d.delta)}`}>{fmtDelta(d.delta)}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{d.users.join(', ') || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DetailBlock>
          )}
        </>
      )}
    </div>
  );
}

function DetailBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <div className="text-xs font-semibold text-slate-600 mb-1">{title}</div>
      <div className="overflow-x-auto border border-slate-200 rounded">{children}</div>
    </div>
  );
}
